import React, { useState, useEffect } from 'react';
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import Button from '@mui/material/Button';
import FeedIcon from '@mui/icons-material/Feed';

function Salegstreport() {
    const [dataBill, setDataBill] = useState([]);
    const [dataItemBtoB, setDataItemBtoB] = useState([]);
    const [dataItemBtoC, setDataItemBtoC] = useState([]);
    const [dataItemHSN, setDataItemHSN] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const { isAuthenticated, companyId, branchId, token, logout, permissions } = useAuth();

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
        setFromDate(getFormattedStartDate());
        setToDate(getFormattedCurrentDate());
    }, [isAuthenticated, token, logout]);

    const getFormattedCurrentDate = () => {
        const currentDate = new Date();
        return currentDate.toISOString().split('T')[0];
    };

    const getFormattedStartDate = () => {
        const monthStartDate = new Date();
        monthStartDate.setDate(1);
        return monthStartDate.toISOString().split('T')[0];
    };


    const fetchTableDataBTOB = async () => {
        try {
            const response = await fetchData('ReportApi', `/SaleGstBtoB/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
            if (response.responseCode === '0000') {
                throw new Error(`Failed to fetch BTOB data`);
            }
            const data = response.response_data;
            setDataItemBtoB(data); // Assuming you are storing it in state
            return data;
        } catch (error) {
            console.error("Error fetching BTOB data:", error);
            throw error; // Propagate error to the caller
        }
    };

    const fetchTableDataBTOC = async () => {
        try {
            const response = await fetchData('ReportApi', `/SaleGstBtoC/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
            if (response.responseCode === '0000') {
                throw new Error(`Failed to fetch BTOC data`);
            }
            const data = response.response_data;
            setDataItemBtoC(data); // Assuming you are storing it in state
            return data;
        } catch (error) {
            console.error("Error fetching BTOC data:", error);
            throw error; // Propagate error to the caller
        }
    };

    const fetchTableDataHSN = async () => {
        try {
            const response = await fetchData('ReportApi', `/SaleGstHSN/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
            if (response.responseCode === '0000') {
                throw new Error(`Failed to fetch BTOC data`);
            }
            const data = response.response_data;
            setDataItemHSN(data); // Assuming you are storing it in state
            return data;
        } catch (error) {
            console.error("Error fetching HSN data:", error);
            throw error; // Propagate error to the caller
        }
    };


    const handleFromDateChange = (e) => {
        setFromDate(e.target.value);
    };

    const handleToDateChange = (e) => {
        setToDate(e.target.value);
    };

    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
        return new Date(dateString).toLocaleDateString('en-GB', options);
    };

    const handleExportExcel = async () => {
        setIsLoading(true);

        try {
            // Fetch all data (in parallel)
            const [btobData, btocData, hsnData] = await Promise.all([
                fetchTableDataBTOB().catch(() => []),
                fetchTableDataBTOC().catch(() => []),
                fetchTableDataHSN().catch(() => []),
            ]);

            // Load Excel template
            const response = await fetch("/SaleGSTTemplate/SaleGSTExport.xlsx");
            const arrayBuffer = await response.arrayBuffer();
            const workbook = new ExcelJS.Workbook();
            await workbook.xlsx.load(arrayBuffer);

            // ------------------ B2B SECTION ------------------
            const sheetNameB = "b2b";
            const worksheetB = workbook.getWorksheet(sheetNameB);
            if (worksheetB && Array.isArray(btobData) && btobData.length > 0) {
                const startRowB = 5;
                const startColB = 1;
                let totalTaxableAmountB = 0;
                let totalTotalAmountB = 0;

                btobData.forEach((item, index) => {
                    const rowNumberB = startRowB + index;
                    const row = worksheetB.getRow(rowNumberB);

                    row.getCell(startColB).value = item.GSTNumber ?? "";
                    row.getCell(startColB + 1).value = item.MasterName ?? "";
                    row.getCell(startColB + 2).value = item.BillNumber ?? "";
                    row.getCell(startColB + 3).value = item.BillDate ? formatDate(item.BillDate) : "";
                    row.getCell(startColB + 4).value = Number(item.TotalAmount || 0).toFixed(2);
                    row.getCell(startColB + 5).value = item.PlaceOfSupply ?? "";
                    row.getCell(startColB + 6).value = "No";
                    row.getCell(startColB + 7).value = Number(item.GSTRate || 0).toFixed(2);
                    row.getCell(startColB + 8).value = "Regular";
                    row.getCell(startColB + 9).value = "";
                    row.getCell(startColB + 10).value = Number(item.GSTRate || 0).toFixed(2);
                    row.getCell(startColB + 11).value = Number(item.TaxableAmount || 0).toFixed(2);

                    totalTotalAmountB += Number(item.TotalAmount || 0);
                    totalTaxableAmountB += Number(item.TaxableAmount || 0);
                    row.commit();
                });

                const totalRow = worksheetB.getRow(3);
                totalRow.getCell(startColB + 4).value = totalTotalAmountB.toFixed(2);
                totalRow.getCell(startColB + 4).font = { bold: true };
                totalRow.getCell(startColB + 11).value = totalTaxableAmountB.toFixed(2);
                totalRow.getCell(startColB + 11).font = { bold: true };
                totalRow.commit();
            }

            // ------------------ B2C SECTION ------------------
            const sheetNameC = "b2cs";
            const worksheetC = workbook.getWorksheet(sheetNameC);
            if (worksheetC && Array.isArray(btocData) && btocData.length > 0) {
                const startRowC = 5;
                const startColC = 2;
                let totalTaxableAmountC = 0;

                btocData.forEach((item, index) => {
                    const row = worksheetC.getRow(startRowC + index);
                    row.getCell(startColC).value = item.PlaceOfSupply ?? "";
                    row.getCell(startColC + 1).value = Number(item.GSTRate || 0).toFixed(2);
                    row.getCell(startColC + 2).value = Number(item.GSTRate || 0).toFixed(2);
                    row.getCell(startColC + 3).value = Number(item.TaxableAmount || 0).toFixed(2);

                    totalTaxableAmountC += Number(item.TaxableAmount || 0);
                    row.commit();
                });

                const totalRow = worksheetC.getRow(3);
                totalRow.getCell(startColC + 3).value = totalTaxableAmountC.toFixed(2);
                totalRow.getCell(startColC + 3).font = { bold: true };
                totalRow.commit();
            }

            // ------------------ HSN SECTION ------------------
            const sheetNamehsn = "hsn";
            const worksheethsn = workbook.getWorksheet(sheetNamehsn);
            if (worksheethsn && Array.isArray(hsnData) && hsnData.length > 0) {
                const startRowhsn = 5;
                const startColhsn = 1;
                let totals = { taxable: 0, cgst: 0, sgst: 0, igst: 0, total: 0 };

                hsnData.forEach((item, index) => {
                    const row = worksheethsn.getRow(startRowhsn + index);
                    row.getCell(startColhsn).value = item.HSNCode ?? "";
                    row.getCell(startColhsn + 2).value = item.UQC ?? "";
                    row.getCell(startColhsn + 3).value = Number(item.Quantity || 0).toFixed(2);
                    row.getCell(startColhsn + 4).value = Number(item.TotalAmount || 0).toFixed(2);
                    row.getCell(startColhsn + 5).value = Number(item.GSTRate || 0).toFixed(2);
                    row.getCell(startColhsn + 6).value = Number(item.TaxableAmount || 0).toFixed(2);
                    row.getCell(startColhsn + 7).value = Number(item.CGSTAmount || 0).toFixed(2);
                    row.getCell(startColhsn + 8).value = Number(item.SGSTAmount || 0).toFixed(2);
                    row.getCell(startColhsn + 9).value = Number(item.IGSTAmount || 0).toFixed(2);

                    totals.taxable += Number(item.TaxableAmount || 0);
                    totals.cgst += Number(item.CGSTAmount || 0);
                    totals.sgst += Number(item.SGSTAmount || 0);
                    totals.igst += Number(item.IGSTAmount || 0);
                    totals.total += Number(item.TotalAmount || 0);
                    row.commit();
                });

                const totalRow = worksheethsn.getRow(3);
                totalRow.getCell(startColhsn + 4).value = totals.total.toFixed(2);
                totalRow.getCell(startColhsn + 6).value = totals.taxable.toFixed(2);
                totalRow.getCell(startColhsn + 7).value = totals.cgst.toFixed(2);
                totalRow.getCell(startColhsn + 8).value = totals.sgst.toFixed(2);
                totalRow.getCell(startColhsn + 9).value = totals.igst.toFixed(2);
                totalRow.eachCell((cell) => (cell.font = { bold: true }));
                totalRow.commit();
            }

            // ------------------ SAVE EXCEL ------------------
            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            const monthYear = getMonthYear(toDate);
            saveAs(blob, `Sale GST(${monthYear}).xlsx`);

            console.log("Excel file exported successfully");
        } catch (error) {
            console.error("Error exporting Excel file:", error);
        } finally {
            setIsLoading(false);
        }
    };



    // const handleExportExcel = async () => {

    //     setIsLoading(true); // Show loading icon
    //     try {

    //         // Wait for all data-fetching functions to complete successfully
    //         await Promise.all([
    //             fetchTableDataBTOB(),
    //             fetchTableDataBTOC(),
    //             fetchTableDataHSN(),
    //         ]);

    //         // Fetch the Excel template
    //         const response = await fetch("/SaleGSTTemplate/SaleGSTExport.xlsx");
    //         const arrayBuffer = await response.arrayBuffer();

    //         // Load the workbook
    //         const workbook = new ExcelJS.Workbook();
    //         await workbook.xlsx.load(arrayBuffer);


    //         // FOR B2B
    //         const sheetNameB = "b2b";
    //         const worksheetB = workbook.getWorksheet(sheetNameB);

    //         if (!worksheetB) {
    //             throw new Error(`Sheet "${sheetNameB}" not found in the template`);
    //         }

    //         // Starting row and column
    //         const startRowB = 5;
    //         const startColB = 1;

    //         let totalTaxableAmountB = 0; // Variable to store the sum of TaxableAmount
    //         let totalTotalAmountB = 0; // Variable to store the sum of TotalAmount

    //         if (!Array.isArray(dataItemBtoB) || dataItemBtoB.length === 0) {
    //             throw new Error("No valid data to export for B2B");
    //         }

    //         // Write data to the worksheet
    //         dataItemBtoB.forEach((item, index) => {
    //             if (!item.BillDate) {
    //                 console.warn(`Invalid data at index ${index}:`, item);
    //                 return;
    //             }

    //             const rowNumberB = startRowB + index;
    //             const excelRowB = worksheetB.getRow(rowNumberB);

    //             excelRowB.getCell(startColB).value = item.GSTNumber; // Column A
    //             excelRowB.getCell(startColB + 1).value = item.MasterName; // Column B
    //             excelRowB.getCell(startColB + 2).value = item.BillNumber; // Column C
    //             excelRowB.getCell(startColB + 3).value = formatDate(item.BillDate); // Column D
    //             excelRowB.getCell(startColB + 4).value = parseFloat(item.TotalAmount.toFixed(2)); // Column E
    //             excelRowB.getCell(startColB + 5).value = item.PlaceOfSupply; // Column F
    //             excelRowB.getCell(startColB + 6).value = "No"; // Column G
    //             excelRowB.getCell(startColB + 7).value = parseFloat(item.GSTRate.toFixed(2)); // Column H
    //             excelRowB.getCell(startColB + 8).value = "Regular"; // Column I
    //             excelRowB.getCell(startColB + 9).value = ""; // Column J
    //             excelRowB.getCell(startColB + 10).value = parseFloat(item.GSTRate.toFixed(2)); // Column K
    //             excelRowB.getCell(startColB + 11).value = parseFloat(item.TaxableAmount.toFixed(2)); // Column L

    //             // Add the amounts to their respective totals
    //             totalTotalAmountB += parseFloat(item.TotalAmount.toFixed(2));
    //             totalTaxableAmountB += parseFloat(item.TaxableAmount.toFixed(2));

    //             excelRowB.commit(); // Commit row changes
    //         });

    //         // Write the sum of TotalAmount to row 3, column E
    //         const totalAmountRowB = worksheetB.getRow(3);
    //         const totalAmountColB = startColB + 4; // Column E
    //         totalAmountRowB.getCell(totalAmountColB).value = totalTotalAmountB;
    //         totalAmountRowB.getCell(totalAmountColB).font = { bold: true }; // Optional: Make the sum bold
    //         totalAmountRowB.commit(); // Commit the row changes

    //         // Write the sum of TaxableAmount to row 3, column L
    //         const totalTaxableRowB = worksheetB.getRow(3);
    //         const totalTaxableColB = startColB + 11; // Column L
    //         totalTaxableRowB.getCell(totalTaxableColB).value = totalTaxableAmountB;
    //         totalTaxableRowB.getCell(totalTaxableColB).font = { bold: true }; // Optional: Make the sum bold
    //         totalTaxableRowB.commit(); // Commit the row changes


    //         // FOR B2C
    //         const sheetNameC = "b2cs";
    //         const worksheetC = workbook.getWorksheet(sheetNameC);

    //         if (!worksheetC) {
    //             throw new Error(`Sheet "${sheetNameC}" not found in the template`);
    //         }

    //         // Starting row and column
    //         const startRowC = 5;
    //         const startColC = 2;

    //         let totalTaxableAmountC = 0; // Variable to store the sum of TaxableAmount           

    //         if (!Array.isArray(dataItemBtoC) || dataItemBtoC.length === 0) {
    //             throw new Error("No valid data to export for B2C");
    //         }

    //         // Write data to the worksheet
    //         dataItemBtoC.forEach((item, index) => {
    //             if (!item.PlaceOfSupply) {
    //                 console.warn(`Invalid data at index ${index}:`, item);
    //                 return;
    //             }

    //             const rowNumberC = startRowC + index;
    //             const excelRowC = worksheetC.getRow(rowNumberC);

    //             excelRowC.getCell(startColC).value = item.PlaceOfSupply; // Column b                
    //             excelRowC.getCell(startColC + 1).value = parseFloat(item.GSTRate.toFixed(2)); // Column C
    //             excelRowC.getCell(startColC + 2).value = parseFloat(item.GSTRate.toFixed(2)); // Column D               
    //             excelRowC.getCell(startColC + 3).value = parseFloat(item.TaxableAmount.toFixed(2)); // Column L

    //             // Add the amounts to their respective totals                
    //             totalTaxableAmountC += parseFloat(item.TaxableAmount.toFixed(2));

    //             excelRowC.commit(); // Commit row changes
    //         });

    //         // Write the sum of TaxableAmount to row 3, column L
    //         const totalTaxableRowC = worksheetC.getRow(3);
    //         const totalTaxableColC = startColC + 3; // Column L
    //         totalTaxableRowC.getCell(totalTaxableColC).value = totalTaxableAmountC;
    //         totalTaxableRowC.getCell(totalTaxableColC).font = { bold: true }; // Optional: Make the sum bold
    //         totalTaxableRowC.commit(); // Commit the row changes


    //         // FOR hsn
    //         const sheetNamehsn = "hsn";
    //         const worksheethsn = workbook.getWorksheet(sheetNamehsn);

    //         if (!worksheethsn) {
    //             throw new Error(`Sheet "${sheetNamehsn}" not found in the template`);
    //         }

    //         // Starting row and column
    //         const startRowhsn = 5;
    //         const startColhsn = 1;

    //         let totalTaxableAmounthsn = 0; // Variable to store the sum of TaxableAmount  
    //         let totalcgsthsn = 0; // Variable to store the sum of cgst  
    //         let totalsgsthsn = 0; // Variable to store the sum of sgst  
    //         let totaligsthsn = 0; // Variable to store the sum of igst  
    //         let totalAmounthsn = 0; // Variable to store the sum of total           

    //         if (!Array.isArray(dataItemHSN) || dataItemHSN.length === 0) {
    //             throw new Error("No valid data to export for B2C");
    //         }

    //         // Write data to the worksheet
    //         dataItemHSN.forEach((item, index) => {
    //             if (!item.TaxableAmount) {
    //                 console.warn(`Invalid data at index ${index}:`, item);
    //                 return;
    //             }

    //             const rowNumberhsn = startRowhsn + index;
    //             const excelRowhsn = worksheethsn.getRow(rowNumberhsn);

    //             excelRowhsn.getCell(startColhsn).value = item.HSNCode; // Column a     
    //             excelRowhsn.getCell(startColhsn + 2).value = item.UQC; // Column C           
    //             excelRowhsn.getCell(startColhsn + 3).value = parseFloat(item.Quantity.toFixed(2)); // Column D
    //             excelRowhsn.getCell(startColhsn + 4).value = parseFloat(item.TotalAmount.toFixed(2)); // Column E               
    //             excelRowhsn.getCell(startColhsn + 5).value = parseFloat(item.GSTRate.toFixed(2)); // Column F
    //             excelRowhsn.getCell(startColhsn + 6).value = parseFloat(item.TaxableAmount.toFixed(2)); // Column G
    //             excelRowhsn.getCell(startColhsn + 7).value = parseFloat(item.CGSTAmount.toFixed(2)); // Column H
    //             excelRowhsn.getCell(startColhsn + 8).value = parseFloat(item.SGSTAmount.toFixed(2)); // Column I
    //             excelRowhsn.getCell(startColhsn + 9).value = parseFloat(item.IGSTAmount.toFixed(2)); // Column J

    //             // Add the amounts to their respective totals                
    //             totalTaxableAmounthsn += parseFloat(item.TaxableAmount.toFixed(2));
    //             totalcgsthsn += parseFloat(item.CGSTAmount.toFixed(2));
    //             totalsgsthsn += parseFloat(item.SGSTAmount.toFixed(2));
    //             totaligsthsn += parseFloat(item.IGSTAmount.toFixed(2));
    //             totalAmounthsn += parseFloat(item.TotalAmount.toFixed(2));

    //             excelRowhsn.commit(); // Commit row changes
    //         });


    //         const totalTotalAmounths = worksheethsn.getRow(3);
    //         const totalTotalColhsn = startColhsn + 4; // Column L
    //         totalTotalAmounths.getCell(totalTotalColhsn).value = totalAmounthsn;
    //         totalTotalAmounths.getCell(totalTotalColhsn).font = { bold: true }; // Optional: Make the sum bold
    //         totalTotalAmounths.commit();


    //         const totalTaxableAmounths = worksheethsn.getRow(3);
    //         const totalTaxableColhsn = startColhsn + 6; // Column L
    //         totalTaxableAmounths.getCell(totalTaxableColhsn).value = totalTaxableAmounthsn;
    //         totalTaxableAmounths.getCell(totalTaxableColhsn).font = { bold: true }; // Optional: Make the sum bold
    //         totalTaxableAmounths.commit();

    //         const totalcgstAmounths = worksheethsn.getRow(3);
    //         const totalcgstColhsn = startColhsn + 7; // Column L
    //         totalcgstAmounths.getCell(totalcgstColhsn).value = totalcgsthsn;
    //         totalcgstAmounths.getCell(totalcgstColhsn).font = { bold: true }; // Optional: Make the sum bold
    //         totalcgstAmounths.commit();

    //         const totalsgstAmounths = worksheethsn.getRow(3);
    //         const totalsgstColhsn = startColhsn + 8; // Column L
    //         totalsgstAmounths.getCell(totalsgstColhsn).value = totalcgsthsn;
    //         totalsgstAmounths.getCell(totalsgstColhsn).font = { bold: true }; // Optional: Make the sum bold
    //         totalsgstAmounths.commit();

    //         const totaligstAmounths = worksheethsn.getRow(3);
    //         const totaligstColhsn = startColhsn + 9; // Column L
    //         totaligstAmounths.getCell(totaligstColhsn).value = totaligsthsn;
    //         totaligstAmounths.getCell(totaligstColhsn).font = { bold: true }; // Optional: Make the sum bold
    //         totaligstAmounths.commit();

    //         // Write the updated workbook to a buffer
    //         const buffer = await workbook.xlsx.writeBuffer();

    //         // Save the file
    //         const blob = new Blob([buffer], {
    //             type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    //         });

    //         const monthYear = getMonthYear(toDate); // Get the formatted month-year string
    //         const filename = `Sale GST(${monthYear}).xlsx`;

    //         saveAs(blob, filename);

    //         console.log("Excel file exported successfully");
    //     } catch (error) {
    //         console.error("Error exporting Excel file:", error);
    //     }
    //     finally {
    //         setIsLoading(false); // Hide loading icon
    //     }
    // };

    const getMonthYear = (dateString) => {
        const date = new Date(dateString);
        const formatter = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' });
        return formatter.format(date); // Returns "December 2024"
    };


    return (
        <div className="w-full mx-auto" style={{ width: '100%', marginTop: '10%' }}>
            <div style={{ padding: '20px', backgroundColor: '#DCE4C9', borderRadius: '8px', margin: 'auto', width: '30%', textAlign: 'center' }}>
                <h6
                    className="uppercase tracking-wide text-black flex-grow linkcolor"
                    style={{ fontFamily: 'Georgia, Cambria, "Times New Roman", Times, serif' }}
                >
                    Sale GST Report
                </h6>

                <div className="w-full mb-2 flex">
                    <div style={{ flex: '2', maxWidth: '50%', paddingRight: '8px', marginBottom: '0', marginTop: '15px' }}>
                        <label className="block uppercase tracking-wide text-black text-xs font-bold mb-2" htmlFor="fromdate">
                            From Date
                        </label>
                        <input
                            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                            type="date" value={fromDate} onChange={handleFromDateChange}
                            style={{ marginTop: '-10px', width: '100%' }}
                        />
                    </div>

                    <div style={{ flex: '2', maxWidth: '50%', marginLeft: '8px', marginBottom: '0', marginTop: '15px' }}>
                        <label className="block uppercase tracking-wide text-black text-xs font-bold mb-2" htmlFor="todate">
                            To Date
                        </label>
                        <input
                            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                            type="date" value={toDate} onChange={handleToDateChange}
                            style={{ marginTop: '-10px', width: '100%' }}
                        />
                    </div>
                </div>

                <Button
                    style={{
                        background: 'linear-gradient(to right, #4D455D,#4D455D)',
                        color: 'black',
                        fontWeight: 'bolder',
                        textTransform: 'none',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
                        maxHeight: '30px',
                        marginLeft: '15px',
                        marginTop: '1.5%'
                    }}
                    onClick={handleExportExcel}
                >
                    <FeedIcon style={{ color: 'white', fontWeight: 'bolder' }} />
                    <b style={{ color: 'white' }}> {isLoading ? "Processing..." : "Export to Excel"}</b>
                </Button>
                {/* {isLoading && <div className="loading-spinner">Loading...</div>} */}
            </div>
        </div>
    );
}

export default Salegstreport;
